/**
 * ChauffeurExcursionsPage — Missions d'excursion du chauffeur
 * Filtre les missions de type excursion depuis l'API unifiée.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import axios from 'axios';
import {
  Compass, MapPin, User, Calendar, Clock,
  ChevronLeft, ChevronRight, Loader2, RefreshCw,
  Eye, AlertTriangle
} from 'lucide-react';
import config from 'config';

const API_URL = config.API_URL;

const STATUS_CFG = {
  assigned:        { label: 'Assignée',       cls: 'bg-blue-500/20 text-blue-400' },
  accepted:        { label: 'Acceptée',       cls: 'bg-purple-500/20 text-purple-400' },
  confirmed:       { label: 'Confirmée',      cls: 'bg-green-500/20 text-green-400' },
  en_route:        { label: 'En route',       cls: 'bg-yellow-500/20 text-yellow-400' },
  on_site:         { label: 'Sur place',      cls: 'bg-orange-500/20 text-orange-400' },
  client_on_board: { label: 'Client à bord',  cls: 'bg-cyan-500/20 text-cyan-400' },
  completed:       { label: 'Terminée',       cls: 'bg-green-500/20 text-green-400' },
  cancelled:       { label: 'Annulée',        cls: 'bg-red-500/20 text-red-400' },
};

const PERIOD_LABELS = {
  upcoming: 'À venir',
  in_progress: 'En cours',
  today: "Aujourd'hui",
  past: 'Passées',
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};
const fmtTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export default function ChauffeurExcursionsPage() {
  const [missions, setMissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('upcoming');
  const [page, setPage] = useState(1);

  const fetchExcursions = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    try {
      const params = new URLSearchParams({ period, page: page.toString(), per_page: '15' });
      const res = await axios.get(`${API_URL}/api/chauffeur/missions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = res.data.missions || [];
      const excursions = all.filter(m => m.service_type === 'excursion');
      setMissions(excursions);
      setTotal(excursions.length);
      setTotalPages(1);
    } catch (err) {
      console.error('Failed to fetch excursions:', err);
    } finally {
      setLoading(false);
    }
  }, [period, page]);

  useEffect(() => {
    fetchExcursions();
  }, [fetchExcursions]);

  return (
    <div className="px-4 py-4 max-w-screen-lg mx-auto" data-testid="chauffeur-excursions-page">
      {/* Title + refresh */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <Compass className="w-4.5 h-4.5 text-emerald-400" /> Excursions
          </h2>
          <p className="text-[#E5E5E5]/40 text-xs">
            {total} excursion(s) {PERIOD_LABELS[period]?.toLowerCase()}
          </p>
        </div>
        <Button
          variant="ghost" size="sm" onClick={fetchExcursions}
          className="text-[#E5E5E5]/50 min-h-[44px] min-w-[44px] touch-manipulation"
          data-testid="refresh-excursions"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Period filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-5 scrollbar-hide">
        {Object.entries(PERIOD_LABELS).map(([key, label]) => (
          <Button
            key={key}
            variant={period === key ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setPeriod(key); setPage(1); }}
            className={`shrink-0 min-h-[44px] px-4 touch-manipulation text-sm ${
              period === key
                ? 'bg-emerald-500 text-white font-semibold'
                : 'border-white/12 text-[#E5E5E5]/70 hover:bg-white/5'
            }`}
            data-testid={`exc-filter-${key}`}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
        </div>
      ) : missions.length === 0 ? (
        <div className="text-center py-16">
          <Compass className="w-10 h-10 mx-auto mb-2 text-[#E5E5E5]/15" />
          <p className="text-[#E5E5E5]/40 text-sm">
            Aucune excursion {PERIOD_LABELS[period]?.toLowerCase()}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {missions.map((m) => {
            const sc = STATUS_CFG[m.status] || STATUS_CFG.assigned;
            return (
              <Card key={m.id} className="bg-[#161616] border-white/6 overflow-hidden" data-testid={`excursion-card-${m.id}`}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="min-w-[52px] text-center shrink-0">
                        <p className="text-emerald-400 text-xl font-bold leading-tight">{fmtTime(m.pickup_datetime)}</p>
                        <p className="text-[#E5E5E5]/30 text-[10px] leading-tight">{fmtDate(m.pickup_datetime)}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-500/15 text-emerald-400">
                        <Compass className="w-3 h-3" /> Excursion
                      </span>
                    </div>
                    <span className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${sc.cls}`}>{sc.label}</span>
                  </div>
                  <div className="px-4 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400 font-mono text-[11px]">{m.booking_ref}</span>
                      {m.client_name && (
                        <span className="text-white/70 text-xs flex items-center gap-1 truncate">
                          <User className="w-3 h-3 text-white/30 shrink-0" /> {m.client_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-4 pb-2.5">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-emerald-400/60 mt-0.5 shrink-0" />
                      <p className="text-[#E5E5E5]/60 text-xs leading-snug truncate">
                        {m.pickup_address || m.pickup_city || '\u2014'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.02] border-t border-white/5">
                    <div className="flex items-center gap-3 text-[10px] text-[#E5E5E5]/35">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {m.passengers} pax</span>
                      {m.has_special_requests && (
                        <span className="text-yellow-400 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /></span>
                      )}
                    </div>
                    <Link
                      to={`/chauffeur/missions/${m.id}`}
                      className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium min-h-[44px] touch-manipulation hover:underline"
                      data-testid={`voir-excursion-${m.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Voir détail
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-5">
          <p className="text-[#E5E5E5]/35 text-xs">Page {page}/{totalPages}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="border-white/12 text-[#E5E5E5] min-h-[44px] min-w-[44px] touch-manipulation"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="border-white/12 text-[#E5E5E5] min-h-[44px] min-w-[44px] touch-manipulation"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
